import { useState, useCallback } from 'react';
import { Button, Text } from '@cowbell/ui';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faCircleCheck,
  faCircleXmark,
} from '@fortawesome/pro-regular-svg-icons';
import { useActionExecution } from '../../hooks/useActionExecution';
import type { FormApprovalAction, AssistantMessage } from '../../types';
import styles from './ActionCard.module.css';

interface Props {
  action: FormApprovalAction;
  message: AssistantMessage;
}

export default function FormApprovalCard({ action, message }: Props) {
  const [outcome, setOutcome] = useState<'accepted' | 'rejected' | null>(null);
  const [error, setError] = useState<string | null>(null);
  const { mutateAsync, isLoading } = useActionExecution();

  const handleAction = useCallback(
    async (decision: 'accepted' | 'rejected') => {
      const actionName =
        decision === 'accepted'
          ? 'accept-quote-changes'
          : 'reject-quote-changes';

      setError(null);
      try {
        await mutateAsync({
          correlationId: message.correlationId,
          messageId: message.messageId,
          action: actionName,
          payload: action as unknown as Record<string, unknown>,
          outcome: decision,
        });
        setOutcome(decision);
      } catch {
        setError('Action failed. Please try again.');
      }
    },
    [mutateAsync, message, action]
  );

  if (outcome) {
    return (
      <div
        className={outcome === 'accepted' ? styles.confirmed : styles.rejected}
      >
        <div className={styles.successMessage}>
          <FontAwesomeIcon
            icon={outcome === 'accepted' ? faCircleCheck : faCircleXmark}
          />
          Changes {outcome}
        </div>
      </div>
    );
  }

  return (
    <div className={styles.card}>
      <div className={styles.cardTitle}>OMNI: Proposed Changes</div>
      <div className={styles.cardSubtitle}>{action.summary}</div>

      {action.changes.map((change, idx) => (
        <div key={idx} className={styles.changeRow}>
          <span className={styles.changeField}>{change.field}</span>
          <span>
            <span className={styles.changeOld}>{change.oldValue}</span>
            {' → '}
            <span className={styles.changeNew}>{change.newValue}</span>
          </span>
        </div>
      ))}

      {error && (
        <Text size="xs" c="red" mt={4}>
          {error}
        </Text>
      )}

      <div className={styles.buttonGroup}>
        <Button
          size="xs"
          color="teal"
          onClick={() => handleAction('accepted')}
          loading={isLoading}
        >
          Accept
        </Button>
        <Button
          size="xs"
          variant="outline"
          color="red"
          onClick={() => handleAction('rejected')}
          loading={isLoading}
        >
          Reject
        </Button>
      </div>
    </div>
  );
}
